import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Input, message, Modal, Row, Select, Space, Table, Tag } from "antd";
import api from "../services/api";
import provinceCities from "../data/provinceCities";
import { PageLoadingOverlay } from "./RoomLoadingScene";

const parseNames = text => text.split(/[\s,，、;；]+/u).map(name => name.trim()).filter(Boolean);

export default function RegionManagement() {
    const [province, setProvince] = useState("广东省");
    const [city, setCity] = useState("深圳市");
    const [regions, setRegions] = useState([]);
    const [regionId, setRegionId] = useState(null);
    const [streets, setStreets] = useState([]);
    const [regionKeys, setRegionKeys] = useState([]);
    const [streetKeys, setStreetKeys] = useState([]);
    const [loadingRegions, setLoadingRegions] = useState(false);
    const [loadingStreets, setLoadingStreets] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [revision, setRevision] = useState(0);
    const [dialog, setDialog] = useState(null);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const saving = useRef(false);
    const region = regions.find(item => item.id === regionId);
    const labels = { regions: "区域", streets: "街道" };
    const names = parseNames(text);

    useEffect(() => {
        let active = true;
        setLoadingRegions(true);
        setLoadError("");
        api.get("/admin/regions", { params: { province, city } }).then(rows => {
            if (!active) return;
            setRegions(rows);
            setRegionId(previous => rows.some(row => row.id === previous) ? previous : rows[0]?.id || null);
            setRegionKeys([]);
        }).catch(error => {
            if (active) { setRegions([]); setRegionId(null); setLoadError(error.error || "区域加载失败"); }
        }).finally(() => { if (active) setLoadingRegions(false); });
        return () => { active = false; };
    }, [province, city, revision]);

    useEffect(() => {
        let active = true;
        setStreets([]);
        setStreetKeys([]);
        if (!regionId) { setLoadingStreets(false); return () => { active = false; }; }
        setLoadingStreets(true);
        api.get("/admin/streets", { params: { region_id: regionId } }).then(rows => {
            if (active) setStreets(rows);
        }).catch(error => {
            if (active) setLoadError(error.error || "街道加载失败");
        }).finally(() => { if (active) setLoadingStreets(false); });
        return () => { active = false; };
    }, [regionId, revision]);

    const openAdd = kind => {
        setText("");
        setDialog({ operation: "add", kind, province, city, region_id: regionId, parent: kind === "regions" ? province + " · " + city : region?.name });
    };
    const openDelete = (kind, keys) => {
        const rows = kind === "regions" ? regions : streets;
        setDialog({ operation: "delete", kind, ids: [...keys], province, city, region_id: regionId, names: rows.filter(row => keys.includes(row.id)).map(row => row.name) });
    };
    const submit = async () => {
        if (saving.current || !dialog) return;
        if (dialog.operation === "add" && (!names.length || names.length > 100 || names.some(name => name.length > 50))) {
            message.error("请输入1至100个名称，每个名称最多50字"); return;
        }
        saving.current = true;
        setBusy(true);
        try {
            const { kind, operation, province: targetProvince, city: targetCity, region_id } = dialog;
            const result = await api.post("/admin/" + kind + (operation === "add" ? "/batch" : "/batch-delete"), {
                ...(kind === "regions" ? { province: targetProvince, city: targetCity } : { region_id }),
                ...(operation === "add" ? { names } : { ids: dialog.ids })
            });
            message.success(operation === "add" ? "已添加 " + result.added + " 条，跳过重复 " + result.skipped + " 条" : "已删除 " + result.deleted + " 条");
            setDialog(null);
            setRevision(value => value + 1);
        } catch (error) { message.error(error.error || "操作失败，请重试"); }
        finally { saving.current = false; setBusy(false); }
    };
    const columns = kind => [
        { title: labels[kind] + "名称", dataIndex: "name", render: (name, row) => kind === "regions"
            ? <Button type="link" style={{ padding: 0, fontWeight: row.id === regionId ? 600 : 400 }} onClick={() => setRegionId(row.id)}>{name}</Button> : name },
        { title: "操作", width: 72, render: (_, row) => <Button size="small" danger disabled={busy} onClick={() => openDelete(kind, [row.id])}>删除</Button> }
    ];
    const toolbar = kind => {
        const keys = kind === "regions" ? regionKeys : streetKeys;
        return <Space wrap style={{ marginBottom: 16 }}>
            <Button type="primary" disabled={busy || (kind === "streets" && !regionId)} onClick={() => openAdd(kind)}>批量添加{labels[kind]}</Button>
            <Button danger disabled={busy || !keys.length} onClick={() => openDelete(kind, keys)}>批量删除{keys.length ? "（" + keys.length + "）" : ""}</Button>
            <span style={{ color: "#8c8c8c" }}>已选 {keys.length} 项</span>
        </Space>;
    };
    return <div>
        {(loadingRegions || loadingStreets) && <PageLoadingOverlay caption="正在加载区域数据，请稍候…" />}
        <Space wrap style={{ marginBottom: 16 }}>
            <Select aria-label="选择省份" showSearch optionFilterProp="label" style={{ width: 180 }} value={province} disabled={busy}
                options={Object.keys(provinceCities).map(value => ({ label: value, value }))} onChange={value => {
                    const firstCity = provinceCities[value][0];
                    setProvince(value); setCity(firstCity); setRegions([]); setRegionId(null); setStreets([]); setRegionKeys([]); setStreetKeys([]);
                }} />
            <Select aria-label="选择城市" showSearch optionFilterProp="label" style={{ width: 180 }} value={city} disabled={busy}
                options={provinceCities[province].map(value => ({ label: value, value }))} onChange={value => {
                setCity(value); setRegions([]); setRegionId(null); setStreets([]); setRegionKeys([]); setStreetKeys([]);
            }} />
            <Tag color="blue">当前区域：{region?.name || "未选择"}</Tag>
            <Button onClick={() => setRevision(value => value + 1)} disabled={busy}>刷新</Button>
        </Space>
        {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
        <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
                <Card title={province + " · " + city + " · 区域（" + regions.length + "）"}>
                    {toolbar("regions")}
                    <Table rowKey="id" size="small" loading={loadingRegions} columns={columns("regions")} dataSource={regions}
                        rowSelection={{ selectedRowKeys: regionKeys, onChange: setRegionKeys, getCheckboxProps: () => ({ disabled: busy }) }}
                        pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
                        onRow={row => ({ style: { background: row.id === regionId ? "#e6f7ff" : undefined } })}
                        locale={{ emptyText: "暂无区域，点击批量添加开始录入" }} />
                </Card>
            </Col>
            <Col xs={24} lg={12}>
                <Card title={region ? region.name + " · 街道（" + streets.length + "）" : "街道列表"}>
                    {toolbar("streets")}
                    <Table rowKey="id" size="small" loading={loadingStreets} columns={columns("streets")} dataSource={streets}
                        rowSelection={{ selectedRowKeys: streetKeys, onChange: setStreetKeys, getCheckboxProps: () => ({ disabled: busy }) }}
                        pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
                        locale={{ emptyText: region ? "暂无街道，点击批量添加开始录入" : "请先选择左侧区域" }} />
                </Card>
            </Col>
        </Row>
        <Modal title={dialog ? (dialog.operation === "add" ? "批量添加" : "确认删除") + labels[dialog.kind] : ""}
            open={!!dialog} onCancel={() => { if (!busy) setDialog(null); }} onOk={submit} confirmLoading={busy}
            closable={!busy} maskClosable={!busy} cancelButtonProps={{ disabled: busy }}
            okText={dialog?.operation === "delete" ? "确认删除" : "确认添加"} okButtonProps={{ danger: dialog?.operation === "delete" }}>
            {dialog?.operation === "add" ? <>
                <p>添加到：{dialog.parent}</p>
                <Input.TextArea aria-label="批量名称" rows={8} value={text} onChange={event => setText(event.target.value)}
                    placeholder={dialog.kind === "regions" ? "输入多个区域，可用换行、空格、逗号、顿号或分号分隔\n南山区 福田区、龙岗区" : "输入多个街道，可用换行、空格、逗号、顿号或分号分隔\n坂田街道 布吉街道、平湖街道"} disabled={busy} />
                <p style={{ color: "#8c8c8c" }}>支持换行、空格、制表符、中英文逗号、顿号和中英文分号；已识别 {names.length} 项，每次最多100项，重复名称自动跳过。</p>
            </> : <>
                <p>将删除选中的 {dialog?.ids?.length || 0} 个{dialog && labels[dialog.kind]}：</p>
                <div style={{ maxHeight: 180, overflow: "auto", marginBottom: 16 }}>{dialog?.names?.join("、")}</div>
                <Alert type="warning" showIcon message="删除后不可撤销" description="如任一选中项有关联街道、楼栋或房源，本次全部保留，请先处理关联数据。" />
            </>}
        </Modal>
    </div>;
}
